import type { ParsedStatement, ParsedTransaction } from "@paisa/types"
import * as XLSX from "xlsx"

/**
 * Detect whether an xlsx file is an American Express credit card statement.
 */
export function isAmexCreditCard(rawContent: string): boolean {
  return /American Express/i.test(rawContent)
}

/**
 * Parse an American Express credit card XLSX statement.
 *
 * Format:
 *   Sheet: "Transaction Details"
 *   Row 0:  "Transaction Details" | "American Express® ... / Jan 01, 2025 to Dec 31, 2025"
 *   Row 4:  "XXXX-XXXXXX-73000"  (masked account number)
 *   Row 6:  headers — Date | Description | Amount | Extended Details | ... | Reference | Category
 *   Row 7+: transactions
 *
 * Amount sign: positive = charge (debit), negative = payment/credit
 * Date format: MM/DD/YYYY
 */
export function parseAmexCreditCard(data: ArrayBuffer): ParsedStatement {
  const workbook = XLSX.read(new Uint8Array(data), { type: "array" })

  // Prefer "Transaction Details" sheet, fall back to first sheet
  const sheetName =
    workbook.SheetNames.find((n) => n === "Transaction Details") ?? workbook.SheetNames[0]
  const sheet = workbook.Sheets[sheetName]
  if (!sheet) throw new Error("No sheet found in Amex workbook")

  const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" })

  const metadata = extractMetadata(rows)

  const headerIdx = findHeaderRow(rows)
  if (headerIdx === -1) throw new Error("Could not find column headers in Amex statement")

  const headerRow = rows[headerIdx] as string[]
  const colMap = mapColumns(headerRow)

  const transactions: ParsedTransaction[] = []
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i] as unknown[]
    if (!row || row.length === 0) continue
    const txn = parseRow(row, colMap)
    if (txn) transactions.push(txn)
  }

  return {
    bank: "amex",
    accountType: "credit_card",
    accountNumber: metadata.accountLast4,
    periodStart: metadata.periodStart,
    periodEnd: metadata.periodEnd,
    transactions,
  }
}

// ── Metadata extraction ──────────────────────────────────────────────────

interface StatementMetadata {
  accountLast4: string | null
  periodStart: string | null
  periodEnd: string | null
}

function extractMetadata(rows: unknown[][]): StatementMetadata {
  let accountLast4: string | null = null
  let periodStart: string | null = null
  let periodEnd: string | null = null

  for (let i = 0; i < Math.min(10, rows.length); i++) {
    const rowText = (rows[i] ?? []).map((c) => String(c ?? "")).join(" ")

    // Account number: "XXXX-XXXXXX-73000" → last 4 = "3000"
    const accountMatch = rowText.match(/XXXX-XXXXXX-(\d+)/)
    if (accountMatch) {
      accountLast4 = accountMatch[1].slice(-4)
    }

    // Period from header: "American Express® ... / Jan 01, 2025 to Dec 31, 2025"
    const periodMatch = rowText.match(/(\w+ \d{2}, \d{4})\s+to\s+(\w+ \d{2}, \d{4})/)
    if (periodMatch) {
      periodStart = parseAmexDate(periodMatch[1])
      periodEnd = parseAmexDate(periodMatch[2])
    }
  }

  return { accountLast4, periodStart, periodEnd }
}

// ── Column detection ─────────────────────────────────────────────────────

interface ColumnMap {
  date: number
  description: number
  amount: number
  reference: number
  category: number
}

function findHeaderRow(rows: unknown[][]): number {
  for (let i = 0; i < Math.min(20, rows.length); i++) {
    const row = (rows[i] ?? []).map((c) => String(c ?? "").trim())
    if (row.includes("Date") && row.includes("Description") && row.includes("Amount")) {
      return i
    }
  }
  return -1
}

function mapColumns(headerRow: string[]): ColumnMap {
  const find = (name: string): number => headerRow.findIndex((h) => h.trim() === name)

  return {
    date: find("Date"),
    description: find("Description"),
    amount: find("Amount"),
    reference: find("Reference"),
    category: find("Category"),
  }
}

// ── Row parsing ──────────────────────────────────────────────────────────

function parseRow(row: unknown[], colMap: ColumnMap): ParsedTransaction | null {
  const rawDate = String(row[colMap.date] ?? "").trim()
  const date = parseAmexDate(rawDate)
  if (!date) return null

  const description = String(row[colMap.description] ?? "")
    .replace(/\n/g, " ")
    .replace(/\s+/g, " ")
    .trim()
  if (!description) return null

  // Amex uses a signed single amount column
  const rawAmount = row[colMap.amount]
  if (rawAmount === "" || rawAmount === null || rawAmount === undefined) return null
  const numAmount =
    typeof rawAmount === "number" ? rawAmount : parseFloat(String(rawAmount).replace(/,/g, ""))
  if (Number.isNaN(numAmount)) return null

  // positive = charge (debit), negative = payment/credit
  const type: "debit" | "credit" = numAmount >= 0 ? "debit" : "credit"
  const amount = Math.abs(numAmount)

  const refNo = String(row[colMap.reference] ?? "").trim() || null

  const sourceCategory =
    colMap.category >= 0 ? String(row[colMap.category] ?? "").trim() || null : null

  return {
    date,
    description,
    amount,
    type,
    referenceNumber: refNo,
    valueDate: null,
    closingBalance: null,
    paymentMethod: null,
    extractedName: description, // already clean merchant name
    upiId: null,
    sourceCategory,
  }
}

// ── Utilities ────────────────────────────────────────────────────────────

/**
 * Parse Amex date formats:
 *   "MM/DD/YYYY"      → "YYYY-MM-DD"   (transaction rows)
 *   "Jan 01, 2025"    → "YYYY-MM-DD"   (metadata header)
 */
function parseAmexDate(raw: string): string | null {
  if (!raw) return null

  // MM/DD/YYYY
  const slashMatch = raw.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (slashMatch) {
    const month = slashMatch[1].padStart(2, "0")
    const day = slashMatch[2].padStart(2, "0")
    return `${slashMatch[3]}-${month}-${day}`
  }

  // "Jan 01, 2025" or "December 31, 2025"
  const monthNames: Record<string, string> = {
    jan: "01",
    feb: "02",
    mar: "03",
    apr: "04",
    may: "05",
    jun: "06",
    jul: "07",
    aug: "08",
    sep: "09",
    oct: "10",
    nov: "11",
    dec: "12",
  }
  const longMatch = raw.trim().match(/^(\w+)\s+(\d{1,2}),?\s+(\d{4})$/)
  if (longMatch) {
    const month = monthNames[longMatch[1].toLowerCase().slice(0, 3)]
    if (month) {
      const day = longMatch[2].padStart(2, "0")
      return `${longMatch[3]}-${month}-${day}`
    }
  }

  return null
}
